import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { Task, updateTaskOnDb, upsertTasks } from "@database/tasks/saveTasks"
import { getTasksFromDb } from "@app/helpers/actions/getTasksFromDb"
import { deleteTaskFromDb } from "@app/helpers/actions/deleteTaskFromDb"
import { useSession } from "next-auth/react"

interface GlobalContextProps {
  tasks: Task[]
  setTasks: Dispatch<SetStateAction<Task[]>>
  refreshTaskDataInContext: () => Promise<void>
  loadingTasks: boolean
  addTasks: (newTasks: Task[]) => Promise<void>
  updateTask: (updatedTask: Partial<Task>) => Promise<void>
  deleteTask: (taskId: string) => Promise<void>
  selectedTaskId: string
  setSelectedTaskId: Dispatch<React.SetStateAction<string>>
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

export const TaskContext: React.FC<GlobalProviderProps> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([])
  const [selectedTaskId, setSelectedTaskId] = useState<string>("")
  const [loadingTasks, setLoadingTasks] = useState(false)
  const { data: session, status } = useSession()

  const refreshTaskDataInContext = useCallback(async () => {
    if (!session?.user) {
      console.log("TaskContext: No session, skipping tasks fetch")
      return
    }

    setLoadingTasks(true)

    try {
      const taskData = await getTasksFromDb()
      if (taskData) {
        const { tasks } = JSON.parse(taskData)
        await setTasks(tasks)
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error)
    } finally {
      setLoadingTasks(false)
    }
  }, [session?.user])

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return
    if (status === "unauthenticated") return

    if (session?.user) {
      // Defer task fetch until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      const deferFetch = () => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              refreshTaskDataInContext()
            },
            { timeout: 2000 }
          ) // Fallback after 2 seconds if browser is busy
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            refreshTaskDataInContext()
          }, 100) // Small delay to allow initial render
        }
      }
      deferFetch()
    }
  }, [session, status, refreshTaskDataInContext])

  const addTasks = useCallback(async (newTasks: Task[]) => {
    // Optimistically add tasks with a loading flag
    setTasks((prev) => [
      ...prev,
      ...newTasks.map((task) => ({ ...task, loading: true })),
    ])

    try {
      // Persist the changes to the database
      await upsertTasks(newTasks)

      // On success, remove the loading flag
      setTasks((prev) =>
        prev.map((task) =>
          newTasks.some((newTask) => newTask.taskId === task.taskId)
            ? { ...task, loading: false }
            : task
        )
      )
    } catch (error) {
      // On failure, remove the optimistically added tasks
      console.error("Failed to add tasks:", error)

      setTasks((prev) =>
        prev.filter(
          (task) => !newTasks.some((newTask) => newTask.taskId === task.taskId)
        )
      )
    }
  }, [])

  const updateTask = useCallback(async (updatedTask: Partial<Task>) => {
    // Optimistically update state
    setTasks((prev) =>
      prev.map((task) =>
        task.taskId === updatedTask.taskId
          ? { ...task, ...updatedTask, loading: true }
          : task
      )
    )

    try {
      // Persist the changes to the database
      await updateTaskOnDb(updatedTask)

      // Set the task's loading state to false on success
      setTasks((prev) =>
        prev.map((task) =>
          task.taskId === updatedTask.taskId
            ? { ...task, loading: false }
            : task
        )
      )
    } catch (error) {
      // Revert the state if the update fails
      console.error("Failed to update task:", error)

      setTasks((prev) =>
        prev.filter((task) => task.taskId !== updatedTask.taskId)
      )
    }
  }, [])

  const deleteTask = useCallback(async (taskId: string) => {
    const { error } = await deleteTaskFromDb(taskId)
    if (error) {
      console.error("Failed to delete task:", error)
      return
    }
    setTasks((prev) => prev.filter((task) => task.taskId !== taskId))
  }, [])

  return (
    <GlobalContext.Provider
      value={{
        tasks,
        setTasks,
        refreshTaskDataInContext,
        loadingTasks,
        addTasks,
        updateTask,
        deleteTask,
        selectedTaskId,
        setSelectedTaskId,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const useTaskContext = () => useContext(GlobalContext)
